import { Component, computed, OnInit, signal } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { finalize } from 'rxjs';
import { LinkServiceProviderRequest, ServiceProviderGroupedList, ServiceProviderSummary } from '../../models/client';
import { ClientService } from '../../shared/client.service';
import { NotificationService } from '../../shared/notification.service';
import { WhatsappService } from '../../whatsapp/whatsapp.service';
import { AppModule } from '../../app.module';

@Component({
  selector: 'app-sp-network',
  imports: [AppModule],
  templateUrl: './sp-network.html',
  styleUrl: './sp-network.less'
})
export class SpNetwork implements OnInit {

  groupedList = signal<ServiceProviderGroupedList | null>(null);
  isLoading = signal<boolean>(false);
  isModalVisible = signal<boolean>(false);
  isLoadingGroupName = signal<boolean>(false);
  isSaving = signal<boolean>(false);
  selectedSP = signal<ServiceProviderSummary | null>(null);
  availableFilterTerm = signal<string>('');

  filteredOtherServiceProviders = computed(() => {
    const term = this.availableFilterTerm().toLowerCase().trim();
    const list = this.groupedList()?.otherServiceProviders ?? [];
    if (term.length < 2) return list;
    return list.filter(sp =>
      sp.clientName.toLowerCase().includes(term) ||
      (sp.addressCity && sp.addressCity.toLowerCase().includes(term)) ||
      sp.clientTel.toLowerCase().includes(term)
    );
  });

  form = new FormGroup({
    whatsappGroupLink: new FormControl('', [Validators.pattern(/^(https?:\/\/)?(www\.)?chat\.whatsapp\.com\/.+$/)]),
    whatsappGroupName: new FormControl('')
  });

  constructor(
    private clientService: ClientService,
    private notification: NotificationService,
    private whatsappService: WhatsappService
  ) {}

  ngOnInit(): void {
    this.loadGroupedList();
  }

  private loadGroupedList(): void {
    this.isLoading.set(true);
    this.clientService.getServiceProviderGroupedList()
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: (data) => {
          this.groupedList.set(data);
        },
        error: (error) => {
          this.notification.handleError(error, 'Error loading service providers');
        }
      });
  }

  public onLinkClick(sp: ServiceProviderSummary): void {
    this.selectedSP.set(sp);
    this.form.reset();
    this.isModalVisible.set(true);
  }

  public onGroupLinkBlur(): void {
    const link = this.form.get('whatsappGroupLink')?.value;
    if (!link) { return; }
    this.isLoadingGroupName.set(true);
    this.form.get('whatsappGroupName')?.setValue('');
    this.form.get('whatsappGroupName')?.disable();
    this.whatsappService.getGroupName(link).subscribe({
      next: (result) => {
        this.form.get('whatsappGroupName')?.setValue(result.groupName || '');
        this.form.get('whatsappGroupName')?.enable();
        this.isLoadingGroupName.set(false);
      },
      error: () => {
        this.form.get('whatsappGroupName')?.enable();
        this.isLoadingGroupName.set(false);
      }
    });
  }

  public handleSave(): void {
    if (this.form.invalid) {
      Object.values(this.form.controls).forEach(control => {
        control.markAsDirty();
        control.updateValueAndValidity();
      });
      this.notification.showError('Please correct the errors in the form before saving.');
      return;
    }

    const sp = this.selectedSP();
    if (!sp) { return; }

    const rawValues = this.form.getRawValue();
    const request: LinkServiceProviderRequest = {
      pricingTemplateId: 0,
      whatsappGroupLink: rawValues.whatsappGroupLink || '',
      whatsappGroupName: rawValues.whatsappGroupName || ''
    };

    this.isSaving.set(true);
    this.clientService.linkServiceProviderToSelf(sp.clientId, request)
      .pipe(finalize(() => this.isSaving.set(false)))
      .subscribe({
        next: (updatedList) => {
          this.groupedList.set(updatedList);
          this.notification.showSuccess(`${sp.clientName} has been linked successfully.`);
          this.isModalVisible.set(false);
          this.form.reset();
        },
        error: (error) => {
          this.notification.handleError(error, 'Error linking service provider');
        }
      });
  }

  public handleCancel(): void {
    this.form.reset();
    this.isModalVisible.set(false);
  }

  public isFieldInvalid(fieldName: string): boolean {
    const field = this.form.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  public getFieldErrorMessage(fieldName: string): string {
    const field = this.form.get(fieldName);
    if (!field || !field.errors) return '';
    if (field.errors['pattern']) return 'Invalid WhatsApp group link format';
    return 'Invalid value';
  }
}
